/**
 * Speakable line for a hangout message, matching voice-worker dialogue extraction:
 * quoted speech wins, otherwise single-asterisk narration is dropped, then
 * parenthetical stage directions are removed.
 */

const DOUBLE_QUOTE_CHARS = new Set([
    '"',
    '\u201C',
    '\u201D',
    '\u201E',
    '\u201F',
    '\u00AB',
    '\u00BB',
    '\uFF02',
    '\u301D',
    '\u301E',
    '\u275D',
    '\u275E',
]);

const balanceTrailingAsterisk = (text) => {
    const asteriskMatches = text.match(/\*/g);
    if (asteriskMatches && (asteriskMatches.length % 2 !== 0)) {
        return `${text}*`;
    }
    return text;
};

const extractSpeakableDialogue = (text) => {
    if (!text) return '';

    const quotedSegments = [];
    let currentSegment = '';
    let inQuotes = false;
    let betweenQuotes = false;
    let interveningNonWs = false;
    let pendingPause = false;

    const pushSegment = (segment) => {
        const trimmed = segment.trim();
        if (!trimmed) return;
        quotedSegments.push({
            text: trimmed,
            pauseBefore: quotedSegments.length > 0 ? pendingPause : false
        });
    };

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (DOUBLE_QUOTE_CHARS.has(ch)) {
            if (inQuotes) {
                pushSegment(currentSegment);
                currentSegment = '';
                inQuotes = false;
                betweenQuotes = true;
                interveningNonWs = false;
            } else {
                if (betweenQuotes) pendingPause = interveningNonWs;
                inQuotes = true;
                betweenQuotes = false;
            }
        } else if (inQuotes) {
            currentSegment += ch;
        } else if (betweenQuotes && !/\s/.test(ch)) {
            interveningNonWs = true;
        }
    }

    if (inQuotes) pushSegment(currentSegment);

    if (quotedSegments.length > 0) {
        return quotedSegments
            .map((seg, index) => {
                if (index === 0) return seg.text;
                return seg.pauseBefore ? ` [pause] ${seg.text}` : ` ${seg.text}`;
            })
            .join('');
    }

    const boldParts = [];
    let withoutBold = text.replace(/\*\*([^*]+)\*\*/g, (match) => {
        boldParts.push(match);
        return `\0BOLD${boldParts.length - 1}\0`;
    });

    if (!/\*[^*]+\*/.test(withoutBold)) {
        return text;
    }

    withoutBold = withoutBold
        .replace(/\*[^*]+\*/g, ' ')
        .replace(/\0BOLD(\d+)\0/g, (_, index) => boldParts[Number(index)] || '')
        .replace(/\s+/g, ' ')
        .trim();

    return withoutBold;
};

export const speakableText = (text) => {
    if (!text) return '';
    const extracted = extractSpeakableDialogue(balanceTrailingAsterisk(String(text)));
    if (!extracted) return '';
    return extracted.replace(/\([^)]+\)/g, '').replace(/\s+/g, ' ').trim();
};
